/**
 * Normalize mobile camera captures for QR upload:
 * - Applies EXIF orientation (createImageBitmap when available)
 * - Converts to JPEG and caps dimensions for reliable server-side decode
 */
async function loadOrientedSource(file, objectUrl) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
      return { kind: 'bitmap', source: bitmap, width: bitmap.width, height: bitmap.height }
    } catch {
      // Fall through to Image element
    }
  }

  const img = await new Promise((resolve, reject) => {
    const el = new Image()
    el.onload = () => resolve(el)
    el.onerror = () => reject(new Error('Failed to load image'))
    el.src = objectUrl
  })
  return { kind: 'image', source: img, width: img.width, height: img.height }
}

export async function normalizeCameraImage(file, { maxDimension = 2800, quality = 0.95 } = {}) {
  if (!file || !(file instanceof Blob)) {
    throw new Error('Invalid image file')
  }

  const url = URL.createObjectURL(file)
  try {
    const loaded = await loadOrientedSource(file, url)
    let { width, height } = loaded

    const maxSide = Math.max(width, height)
    if (maxSide > maxDimension) {
      const scale = maxDimension / maxSide
      width = Math.round(width * scale)
      height = Math.round(height * scale)
    }

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    ctx.drawImage(loaded.source, 0, 0, width, height)
    loaded.source.close?.()

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (result) => (result ? resolve(result) : reject(new Error('Failed to process image'))),
        'image/jpeg',
        quality
      )
    })

    const baseName = (file.name || 'camera_capture').replace(/\.[^.]+$/, '')
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() })
  } finally {
    URL.revokeObjectURL(url)
  }
}

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|heic|heif)$/i
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024
/** QR resolve uploads — keep small for mobile networks and reverse proxies */
const QR_SCAN_TARGET_BYTES = 900 * 1024
const QR_SCAN_MAX_BYTES = 2 * 1024 * 1024

export async function compressQrScanImage(file, {
  maxDimension = 1800,
  quality = 0.82,
  targetBytes = QR_SCAN_TARGET_BYTES,
} = {}) {
  let result = await normalizeCameraImage(file, { maxDimension, quality })
  let q = quality
  while (result.size > targetBytes && q > 0.45) {
    q -= 0.08
    maxDimension = Math.max(1200, Math.round(maxDimension * 0.88))
    result = await normalizeCameraImage(file, { maxDimension, quality: q })
  }
  if (result.size > QR_SCAN_MAX_BYTES) {
    throw new Error('Image is too large after compression. Move closer to the QR or use better lighting.')
  }
  return result
}

export function isAcceptedImageFile(file) {
  if (!file) return false
  if (file.type?.startsWith('image/')) return true
  if (!file.type || file.type === 'application/octet-stream') {
    return IMAGE_EXT.test(file.name || '')
  }
  return IMAGE_EXT.test(file.name || '')
}

export function isAcceptedScanFile(file) {
  if (!file) return false
  if (file.type === 'application/pdf') return true
  return isAcceptedImageFile(file)
}

/** Camera captures for QR status scan — always re-encode and cap size for mobile upload. */
export async function prepareQrScanImageFile(file, { fromCamera = false } = {}) {
  if (!file) return file
  if (file.type === 'application/pdf' || (file.name || '').toLowerCase().endsWith('.pdf')) {
    return file
  }

  const looksLikeImage =
    isAcceptedImageFile(file) ||
    fromCamera ||
    !file.type ||
    file.type === 'application/octet-stream'

  if (!looksLikeImage) {
    return file
  }

  return compressQrScanImage(file, {
    maxDimension: fromCamera ? 1800 : 2000,
    quality: fromCamera ? 0.82 : 0.88,
  })
}

/** Camera captures are re-encoded with EXIF fix; gallery uploads stay original when small enough. */
export async function prepareScanImageFile(file, { fromCamera = false } = {}) {
  if (!file) return file;
  if (file.type === 'application/pdf' || (file.name || '').toLowerCase().endsWith('.pdf')) {
    return file;
  }

  const looksLikeImage =
    isAcceptedImageFile(file) ||
    fromCamera ||
    !file.type ||
    file.type === 'application/octet-stream';

  if (!looksLikeImage) {
    return file;
  }

  if (fromCamera || !file.type || file.type === 'application/octet-stream' || file.size > MAX_UPLOAD_BYTES) {
    return normalizeCameraImage(file, {
      maxDimension: fromCamera ? 3200 : 2800,
      quality: 0.92,
    });
  }

  return file;
}
